import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from './utils/Toast';
import Loader from './utils/Loader';
import Navbar from './utils/Navbr';
import './style/AddDomain.css';
import { useNavigate } from 'react-router-dom';

const AddDomain = () => {
    // --- State ---
    const [loading, setLoading] = useState(true);
    const [domains, setDomains] = useState([]);
    const [user, setUser] = useState({});
    const navigate = useNavigate();
    const [tDomain, setTDomain] = useState('');
    const [newDomain, setNewDomain] = useState('');
    const [isAdding, setIsAdding] = useState(false);

    const getCookie = (name) => {
        const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
        return match ? decodeURIComponent(match[1]) : null;
    };

    // --- Fetch Domains ---
    const fetchDomains = async () => {
        setLoading(true);
        const authToken = getCookie('authToken');
        try {
            const res = await axios.post('https://publisher.cashbolt.in.net/server/', {
                action: 'fetchDomain',
                authToken
            });

            if (res.data.status) {
                setDomains(res.data.domains);
                setTDomain(res.data.tDomain);
            }
        } catch (error) {

        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const authToken = getCookie('authToken');
        const fetchData = async () => {
            try {
                setLoading(true);
                const userResponse = await axios.post('https://publisher.cashbolt.in.net/server/', {
                    action: 'checkAuth',
                    authToken
                });

                if (userResponse.data.status === false) {
                    toast.error(userResponse.data.message || 'Session expired', 3000);
                    navigate('/login');
                    return;
                } else {
                    setUser(userResponse.data.res);
                    await fetchDomains();
                }
            } catch (error) {
                console.error(error);
                toast.error('Failed to load dashboard data');
                navigate('/login');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    // --- Handlers ---
    const handleAddDomain = async () => {
        // Basic validation
        const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]\.[a-zA-Z]{2,}$/;
        if (!newDomain || !domainRegex.test(newDomain)) {
            return toast.warning("Please enter a valid domain (e.g. mysite.com)");
        }

        setIsAdding(true);
        const authToken = getCookie('authToken');

        try {
            const res = await axios.post('https://publisher.cashbolt.in.net/server/', {
                action: 'addDomain',
                authToken,
                domain: newDomain
            });

            if (res.data.status) {
                toast.success("Domain Added Successfully!");
                setNewDomain('');
                fetchDomains(); // Refresh list
            } else {
                toast.error(res.data.message || "Failed to add domain");
            }
        } catch (error) {
            toast.error("Server Error");
        } finally {
            setIsAdding(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this domain? Existing campaigns using it may break.")) return;

        try {
            const res = await axios.post('https://publisher.cashbolt.in.net/server/', {
                action: 'deleteDomain',
                authToken: getCookie('authToken'),
                domain_id: id
            });

            if (res.data.status) {
                setDomains(prev => prev.filter(d => d.id !== id));
                toast.success("Domain Deleted");
            } else {
                toast.error("Delete Failed");
            }
        } catch (error) {
            toast.error("Server Error");
        }
    };

    return (
        <div className="ad-wrapper_ad">
            <Navbar name={user?.name || 'Partner'} role={user?.role || 'Publisher'} />

            <div className="ad-bg-orb ad-orb-1"></div>
            <div className="ad-bg-orb ad-orb-2"></div>

            <div className="ad-container_ad">

                <div className="ad-header_ad">
                    <h2>Manage <span className="ad-highlight">Domains</span></h2>
                    <p>Connect custom domains to white-label your campaigns</p>
                </div>

                {/* --- 1. Guide Section --- */}
                <div className="ad-guide-card_ad">
                    <div className="ad-guide-content">
                        <h3><span className="ad-icon">⚙️</span> DNS Configuration Guide</h3>
                        <p>Before adding your domain, you must point it to our server. Log in to your domain registrar (GoDaddy, Namecheap, etc.) and add the following record:</p>

                        <div className="ad-dns-record">
                            <div className="ad-record-item">
                                <span className="lbl">Type</span>
                                <span className="val">CNAME</span>
                            </div>
                            <div className="ad-record-item">
                                <span className="lbl">Name / Host</span>
                                <span className="val">@</span>
                            </div>
                            <div className="ad-record-item">
                                <span className="lbl">Value / Target</span>
                                <span className="val highlight">{tDomain}</span>
                            </div>
                            <div className="ad-record-item">
                                <span className="lbl">TTL</span>
                                <span className="val">Automatic</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* --- 2. Add Domain Input --- */}
                <div className="ad-input-card_ad">
                    <h3>Add New Domain</h3>
                    <div className="ad-input-group">
                        <div className="ad-input-wrapper">
                            <span className="ad-prefix">https://</span>
                            <input
                                type="text"
                                placeholder="your-domain.com"
                                value={newDomain}
                                onChange={(e) => setNewDomain(e.target.value.toLowerCase())}
                            />
                        </div>
                        <button
                            className="ad-add-btn"
                            onClick={handleAddDomain}
                            disabled={isAdding}
                        >
                            {isAdding ? 'VERIFYING...' : 'ADD DOMAIN'}
                            <div className="ad-wave"></div>
                        </button>
                    </div>
                    <p className="ad-note">* Domain propagation may take up to 24 hours.</p>
                </div>

                {/* --- 3. Domain List Table --- */}
                <div className="ad-table-card_ad">
                    {loading ? <div className="loader-container_perf"><div className="spinner_perf"></div><p>Loading Data...</p></div> : (
                        <div className="ad-table-scroll">
                            <table className="ad-table">
                                <thead>
                                    <tr>
                                        <th>Domain URL</th>
                                        <th>Date Added</th>
                                        <th className="text-center_ad">Status</th>
                                        <th className="text-center_ad">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {domains.length > 0 ? (
                                        domains.map((dom) => (
                                            <tr key={dom.id} className="ad-row">
                                                <td className="ad-url-cell">
                                                    <span className="ad-globe">🌐</span>
                                                    {dom.domain}
                                                </td>
                                                <td className="ad-date">{dom.createdAt}</td>
                                                <td className="text-center_ad">
                                                    <span className={`ad-status-badge ${dom.status}`}>
                                                        {dom.status === 'active' ? '● Active' : '○ Pending'}
                                                    </span>
                                                </td>
                                                <td className="text-center_ad">
                                                    <button
                                                        className="ad-delete-btn"
                                                        title="Delete Domain"
                                                        onClick={() => handleDelete(dom.id)}
                                                    >
                                                        🗑️
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="4" className="ad-no-data">No domains added yet.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
};

export default AddDomain;